<template>
  <page-wrapper :title="title" class="service-unavailable-date-page">
    <div class="container wrapper">
      <service-list-item
        v-if="service"
        v-bind="service"
        class="mb-4"
      ></service-list-item>
      <h3 class="mb-4">{{ title }}</h3>
      <div class="row">
        <div class="col-lg-4 mb-4">
          <h4 class="mb-4">ทุก ๆ วันที่</h4>
          <div class="date-calendar">
            <el-checkbox-group v-model="form.unavailableWorkdaysInMonth">
              <el-checkbox-button
                v-for="date in dates"
                :key="date"
                :label="date"
                >{{ date }}</el-checkbox-button
              >
            </el-checkbox-group>
          </div>
        </div>
        <div class="col-lg-4 mb-4">
          <h4 class="mb-4">ทุก ๆ วัน</h4>
          <div class="day-calendar">
            <el-checkbox-group v-model="form.unavailableWorkdaysInWeek">
              <el-checkbox-button
                v-for="day in days"
                :key="day.value"
                :label="day.value"
                >{{ day.label }}</el-checkbox-button
              >
            </el-checkbox-group>
          </div>
        </div>
        <div class="col-lg-4 mb-4">
          <h4 class="mb-4">ทุก ๆ ช่วงเวลา</h4>
          <div class="time-calendar">
            <el-checkbox-group v-model="form.unavailableTimes">
              <el-checkbox-button
                v-for="time in times"
                :key="time.value"
                :label="time.value"
                >{{ time.label }}</el-checkbox-button
              >
            </el-checkbox-group>
          </div>
        </div>
        <div class="col-lg-4 mb-4">
          <h4 class="mb-4">เฉพาะวันที่</h4>
          <date-picker
            :value="selectedDate"
            class="mb-4"
            @input="handleChangeSelectedDate"
          ></date-picker>
          <div
            v-for="(dateTime, index) in unavailableDateTimes"
            :key="dateTime"
            class="mb-4"
          >
            {{ dateTime }}
            <sc-button
              class="btn-remove"
              variant="danger"
              size="sm"
              @click="handleRemove(index)"
              >ลบ</sc-button
            >
          </div>
        </div>
      </div>
      <div class="text-end">
        <sc-button variant="primary" @click="handleClickSave">บันทึก</sc-button>
      </div>
    </div>
  </page-wrapper>
</template>

<script>
// Third-party
import {
  CheckboxButton as ElCheckboxButton,
  CheckboxGroup as ElCheckboxGroup
} from "element-ui";
import { DateTime } from "luxon";

// Internal
import { baanRueJaiClient } from "@/dependencies";
import DatePicker from "@/lib/components/DatePicker";
import PageWrapper from "@/lib/components/PageWrapper";
import ScButton from "@/lib/components/ScButton";
import { toLocaleTH } from "@/lib/dateTime";
import { datesOfMonth, daysOfWeek, timeOfDay } from "@/lib/mapper";

// Local
import ServiceListItem from "../components/ServiceListItem";

export default {
  components: {
    PageWrapper,
    ScButton,
    ElCheckboxButton,
    ElCheckboxGroup,
    DatePicker,
    ServiceListItem
  },
  data() {
    const title = "วันที่ไม่สามารถนัดหมายบริการ";
    const form = {
      unavailableWorkdaysInMonth: [],
      unavailableWorkdaysInWeek: [],
      unavailableDateTimes: [],
      unavailableTimes: []
    };
    const selectedDate = null;
    const service = null;
    const workdays = null;

    return {
      title,
      form,
      dates: datesOfMonth,
      days: daysOfWeek,
      times: timeOfDay,
      selectedDate,
      service,
      workdays
    };
  },
  computed: {
    unavailableDateTimes() {
      return this.form.unavailableDateTimes
        .slice()
        .sort((a, b) => a.toMillis() - b.toMillis())
        .map(dateTime => toLocaleTH(dateTime).toFormat("dd MMMM yyyy"));
    },
    sortedUnavailableDateTimes() {
      return this.form.unavailableDateTimes
        .slice()
        .sort((a, b) => a.toMillis() - b.toMillis());
    }
  },
  created() {
    this.setup();
  },
  methods: {
    setup() {
      this.setupData();
    },
    async setupData() {
      await Promise.all([this.setWorkdays(), this.setupService()]);
      await this.setupForm();
    },
    async setWorkdays() {
      const data = await this.fetchWorkdays();
      this.workdays = data;
    },
    async setupService() {
      const data = await this.fetchService();

      const {
        id,
        name,
        short_description: shortDescription,
        description,
        icon_url: iconUrl
      } = data;
      const provider = {
        ...data.provider,
        logoUrl: data.provider.logo_url
      };

      this.service = {
        id,
        iconUrl,
        name,
        shortDescription,
        description,
        provider
      };
    },
    async setupForm() {
      this.form.unavailableWorkdaysInMonth = this.workdays.unavailable_workdays_in_month;
      this.form.unavailableWorkdaysInWeek = this.workdays.unavailable_workdays_in_week;
      this.form.unavailableTimes = this.workdays.unavailable_times;
      this.form.unavailableDateTimes = this.workdays.unavailable_date_times.map(
        d => DateTime.fromISO(d)
      );
    },
    async fetchService() {
      const { id } = this.$route.params;
      const response = await baanRueJaiClient.admin.subscriptions.services.get(
        id
      );

      return response.data.result;
    },
    async handleClickSave() {
      const { id } = this.$route.params;
      const data = {
        unavailable_workdays_in_month: this.form.unavailableWorkdaysInMonth,
        unavailable_workdays_in_week: this.form.unavailableWorkdaysInWeek,
        unavailable_times: this.form.unavailableTimes,
        unavailable_date_times: this.form.unavailableDateTimes.map(d => d.toISO())
      };
      try {
        await baanRueJaiClient.admin.subscriptions.services.putWorkdays(
          id,
          data
        );

        this.$message.success("บันทึกเรียบร้อย");
      } catch (error) {
        this.$message.error("ไม่สามารถเลือกวันที่ไม่สามารถนัดหมายได้");
      }
    },
    handleRemove(index) {
      const target = this.sortedUnavailableDateTimes[index];
      this.form.unavailableDateTimes = this.form.unavailableDateTimes.filter(
        (d) => d.valueOf() !== target.valueOf()
      );
    },
    handleChangeSelectedDate(date) {
      if (
        this.form.unavailableDateTimes.find(d => d.valueOf() === date.valueOf())
      ) {
        return;
      }

      this.form.unavailableDateTimes.push(date);
      this.selectedDate = null;
    },

    async fetchWorkdays() {
      const { id } = this.$route.params;
      const response = await baanRueJaiClient.admin.subscriptions.services.getWorkdays(
        id
      );

      return response.data.result;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";
.service-unavailable-date-page {
  .wrapper {
    padding-top: $space;
    padding-bottom: $space;
  }

  .el-checkbox-button:hover > .el-checkbox-button__inner {
    color: $danger-color;
  }

  .el-checkbox-button.is-checked > .el-checkbox-button__inner {
    background: $danger-color;
    &:hover {
      color: white;
    }
  }

  .date-calendar {
    .el-checkbox-group {
      display: grid;

      grid-template-columns: repeat(6, 40px);
      padding: 10px;
    }

    .el-checkbox-group > .el-checkbox-button {
      margin-bottom: 0;
      padding: 5px;
    }

    .el-checkbox-button.is-checked > .el-checkbox-button__inner {
      border: 0;
      box-shadow: none;

      &:hover {
        color: white;
      }
    }

    .el-checkbox-button > .el-checkbox-button__inner {
      border-radius: 50%;

      border: 0;
      padding: 0;
      width: 30px;
      height: 30px;

      line-height: 30px;
    }
  }

  .day-calendar {
    .el-checkbox-button,
    .el-checkbox-button > .el-checkbox-button__inner {
      border-radius: 0;
      display: block;
    }

    .el-checkbox-button > .el-checkbox-button__inner {
      border-left: 1px solid #dadada;
    }

    .el-checkbox-button.is-checked > .el-checkbox-button__inner {
      box-shadow: none;
      border-color: $danger-color;

      &:hover {
        color: white;
      }
    }
  }

  .time-calendar {
    .el-checkbox-button,
    .el-checkbox-button > .el-checkbox-button__inner {
      border-radius: 0;
      display: block;
    }

    .el-checkbox-button > .el-checkbox-button__inner {
      border-left: 1px solid #dadada;
    }

    .el-checkbox-button.is-checked > .el-checkbox-button__inner {
      box-shadow: none;
      border-color: $danger-color;

      &:hover {
        color: white;
      }
    }
  }

  .btn-remove {
    float: right;
  }
}
</style>
